/* Dynamic Plot code copyright 2024 (c) Tuva Labs Inc.
 *
 * created by cmiller
 */

import { ElemCard } from "./ElemCard";
import ElemPlayContainer from "./ElemPlayContainer";
import { ElemNewCard } from "./ElemNewCard";
import { ElemEditableView } from "./editable/ElemEditableView";
import CaseCard from "../../components/elem-play/editable/CaseCard";

/**
 * The ElemHomeView object creates small cards, one per case (class ElemCard),
 * and arranges the small cards in the 'Cards' area of Tuva Jr Play View.
 * */
export class ElemHomeView {

    constructor( Dyn, container ) {

        // references to other objects
        this.Dyn = Dyn;
        this.container = container;
        this.bounds = new Dyn.mathUtil.Rect(0,0,0,0); // area for cards laid out on table
        this.smallCards = []; // one card for each case not filtered
        this.caseToCardIndex = []; // needed if cases are filtered out
        this.iconStyler = new Dyn.IconStyler();
        this.glowElement = null;
        //this.filteredCasesDisplay = null;

        // For edit mode
        this.editAttributeCard = null; // small card to add/edit attributes
        this.plusCaseCard = null; // small card to add cases
    }

    destroy() {
        this.Dyn = null;
        this.container = null;
    }

    initVisuals() {
        let customIconPath = this.container.dataSet.getCustomCaseIcon(),
            paper = this.container.paper;
        if( customIconPath ) {
            this.iconStyler.setCustomCaseIcon( paper, customIconPath );
        }
        //this.filteredCasesDisplay = new FilteredCasesDisplay( this.Dyn, this.container );
    }

    updateVisuals() {
        // update the filter display
        // this.filteredCasesDisplay.update(
        //     this.container.dataSet, this.bounds );
        // position cards above the filter display
        this.makeAndPositionCards();
    }

    resize( newSize ) {
        let Dyn = this.Dyn;
        this.bounds.copyRect( newSize );
        this.bounds.addDebugRect( this.container.paper );
        Dyn.assertValidRect( this.bounds );
        this.updateVisuals();
    }

    /**
     * Position or re-position the small cards in the home area, creating them if needed,
     * except for those cards that have been dragged or are in the big Card area or plot.
     * The position of the outlines determine where a card goes when returned "home".
     * Used for first time initialization, restore from saved plot, and after view size changes.
     * Creates cards if they don't exist (typically when loaded for the first time).
     */
    makeAndPositionCards() {
        const numCards = this.container.dataSet.getNumCasesAfterFilter(), // number of case cards to show 1...n
            numCases = this.container.dataSet.getNumCasesPreFilter(), // number of cases in the data set, some may be filtered out
            // include edit attribute card and plus case card if in edit mode
            numCardsWEditing = numCards + (this.container.isEditable? 1 : 0) + (this.container.isEditing? 1 : 0);
        const border = ElemCard.cardBorder,
            w = ElemCard.cardWidth,
            h = ElemCard.cardHeight,
            colWidth = w+border,
            cols = Math.max( 1, Math.floor( (this.bounds.width - border) / colWidth )),
            rows = Math.max( 1, Math.ceil( numCardsWEditing/cols )),
            rowHeightTiled = h+border,
            rowHeightFitted = (this.bounds.height - (rowHeightTiled+border))/(rows-1),
            leftBorder = (this.bounds.width - (cols * w) - (cols-1)*border)/2;
        // if needed, overlap cards to fit
        let rowHeight = Math.max( 1, Math.min( rowHeightTiled, rowHeightFitted )),
            Dyn = this.Dyn,
            cardRect = new Dyn.mathUtil.Rect( this.bounds.x, 0, w, h ).offset( leftBorder, border ),
            smallCardIndex = 0; // warning: if filtering, smallCardIndex !== caseIndex
        const nextCardRect = () => { // move the card rect to the next available position in the home view
            if( (cardRect.right() + colWidth) > this.bounds.right() ) {
                cardRect.x = this.bounds.x + leftBorder;
                cardRect.y += rowHeight; // next row
            } else {
                cardRect.x += colWidth; // next column
            }
        }
        const setCardHomePosition = ( card, cardRect ) => {
            if( card.isDragged ) { // restore to same relative drag position
                let n = card._normalizedToViewCoord( card.dragCoords.x, card.dragCoords.y );
                card.back.transform( "t" + n.x + "," + n.y );
                Dyn.assert( !card.isHome, "Card is home; " + card.getCardState( this.container ) );
            } else { // move to home position
                card.back.transform( "t" + cardRect.x + "," + cardRect.y );
                Dyn.assert( card.isHome, "Card not home; " + card.getCardState( this.container ) );
            }
        }

        // Add/update/destroy edit attribute card, 1st card if in edit mode
        if( this._addEditAttributeCard( cardRect )) {
            nextCardRect();
        }

        // position all cards, creating them if needed
        for( let caseIndex = 0; caseIndex<numCases; caseIndex++ ) {
            if( this.container.dataSet.isCaseFilteredOut(caseIndex)) {
                this.caseToCardIndex[ caseIndex ] = -1; // skip this case
            } else {
                let card = this.smallCards[smallCardIndex]; // may be undefined
                this.caseToCardIndex[ caseIndex ] = smallCardIndex;
                card = this._addSmallCard( card, caseIndex, smallCardIndex, cardRect );
                if( !(this.container.play.isCardInPlayArea( smallCardIndex ) // is in a bin? playView positions it
                    || this.container.big.isCardInBigCardArea( caseIndex ) // is in a big card? bigCardView positions it
                    || card.isMissing) ) { // is missing? playView hides card, no need to position
                    setCardHomePosition( card, cardRect );
                }
                nextCardRect();
                this.Dyn.assert( ! this.container.dataSet.isCaseFilteredOut(card.caseIndex), "case index should be filtered out: "+card.caseIndex+" for card: "+card.smallCardIndex);
                ++smallCardIndex;
            }
        }
        // remove extra cards (e.g. if filter was changed)ś
        while( this.smallCards.length > numCards ) {
            this.container.removeHideCard( this.smallCards.pop());
        }
        this.caseToCardIndex.length = numCases;
        Dyn.assert( numCards <= numCases );
        Dyn.assert( numCards === this.smallCards.length );

        // Add/update/destroy plus case card, last card if in edit mode
        if( this._addPlusCaseCard( cardRect, numCases )) {
            nextCardRect();
        }
    }

    /** If needed, construct a small card object, add to our array of smallCards */
    _addSmallCard( card, caseIndex, smallCardIndex, rect ) {
        if( card && ( card.caseIndex !== caseIndex || card.smallCardIndex !== smallCardIndex )) {
            card.destroy(); // probably the filter changed, we must delete then re-create the card
            this.smallCards[smallCardIndex] = card = null;
        }
        if( !card ) {
            card = new ElemCard( caseIndex, smallCardIndex, rect, /*corner,*/ this.container, this.iconStyler );
            this.smallCards[smallCardIndex] = card;
        }
        this.Dyn.assert( this.smallCards.length <= this.caseToCardIndex.length );
        card.outline.attr( { x: rect.x, y: rect.y } );
        return card;
    }

    // If needed, construct the Edit Attribute card to add/edit attributes
    _addEditAttributeCard( rect ) {
        if( this.container.isEditable && !this.editAttributeCard ) {
            this.editAttributeCard = new ElemNewCard( rect, this.container );
        } else if( ! this.container.isEditable && this.editAttributeCard ) {
            this.editAttributeCard.destroy();
            this.editAttributeCard = null;
        }
        this.editAttributeCard?.back.transform( "t" + rect.x + "," + rect.y );
        return Boolean( this.editAttributeCard );
    }

    // If needed, construct the Plus Case card to add/edit cases
    _addPlusCaseCard( rect, numCases ) {
        let destroyPlusCard = false;
        if(this.container.isEditing) {
            // Show when number of cases is less than the maximum number of cases
            if(numCases < CaseCard.MAX_CASES) {
                if(!this.plusCaseCard) {
                    this.plusCaseCard = new ElemNewCard( rect, this.container, 'case' );
                } else {
                    this.plusCaseCard?.back.transform( "t" + rect.x + "," + rect.y );
                }
            } else {
                destroyPlusCard = true;
            }
        } else {
            destroyPlusCard = true;
        }
        if(destroyPlusCard) {
            this.plusCaseCard?.destroy();
            this.plusCaseCard = null;
        }
        return Boolean( this.plusCaseCard );
    }

    /** arrange small cards in z-order, card backs over outlines */
    layerElements( layerCardBacks, layerNo ) {
        switch( layerNo ) {
            case 0:
                this.editAttributeCard?.back.toFront(); // 1st card if in edit mode
                this.smallCards.forEach( ( c ) => c.outline.toFront() );
                break;
            case 1:
                if( layerCardBacks ) {
                    this.smallCards.forEach( ( c ) => c.back.toFront() );
                }
                this.plusCaseCard?.back.toFront(); // last card if in edit mode
                break;
        }
    }

    highlightCardHome( onOff, smallCardIndex ) {
        this.Dyn.assertValidArray( this.smallCards, smallCardIndex );
        this.smallCards[smallCardIndex].outline.attr({ fill:onOff?ElemCard.cardOutlineDropFill:ElemCard.cardOutlineFill})
    }

    // hideCardUnderBigCard( smallCardIndex, bigCardBBox = null /*optional*/ ) {
    //     this.smallCards[smallCardIndex]?.hideCardUnderBigCard( bigCardBBox, this.Dyn );
    // }

    /** animate the small card to the home position, or optionally the last plot position */
    animateCardToHome( smallCardIndex, speed=undefined, toPlotPosition=false ) {
        let Dyn = this.Dyn,
            card = this.smallCards[smallCardIndex],
            handleAnimationEnd=null,
            homeBBox, color=null;
        if( toPlotPosition ) {
            // card has been moved/opened while in a Plot state. Return it to the plotted position
            homeBBox = card.getPlotCoords();
            Dyn.assert( ! (card.isMissing || card.isHome ));
            handleAnimationEnd = () => {
                card.setIsPlotted( undefined, undefined, undefined, true /* returned to plot */ );
                card.showHideCard(this.container.options.isIconOnly, Dyn);
            }
            card.setIsDragged( homeBBox.x, homeBBox.y );
            card.showHideCard(this.container.options.isIconOnly, Dyn);
        } else {
            homeBBox = card.getHomeBBox()
            // card.setIsMissing(false);
            card.setIsHome( true );
            // card.setIsDragged( false );
            // card.setIsBig( false );
            card.showHideCard(false, Dyn);
            color = this.container.iconColor();
        }
        card.animateCardTo(
            "t"+homeBBox.x+","+homeBBox.y,
            speed || this.container.fastAnimation(),
            color,
            handleAnimationEnd );
    }

    /** reset the small cards state and animate each one to the home ("Cards") position */
    resetSmallCards() {
        this.Dyn.assert( !this.container.isIconOnly ); // caller should clear option first
        this.smallCards.forEach( (card) => {
            this.Dyn.assert( ! this.container.dataSet.isCaseFilteredOut(card.caseIndex), "case index should be filtered out: "+card.caseIndex+" for card: "+card.smallCardIndex);
            let homeBBox = card.getHomeBBox();
            // card.setIsMissing(false);
            card.setIsHome();
            // card.setIsDragged( false );
            card.showHideCard(false, this.Dyn);
            card.animateCardTo( "t"+homeBBox.x+","+homeBBox.y,
                this.container.slowAnimation(), this.container.iconColor());
        });
        this.container.setState( { plot:"home" });
    }

    allCardsAreHome() {
        return !this.smallCards.find((card) => !card.isHome);
    }

    setCardsIconOnly( wantIconOnly ) {
        this.smallCards.forEach( (card) => {
            card.showHideCard( wantIconOnly, this.Dyn );
        });
    }

    isCardOverlappingHome( cardBBox, smallCardIndex ) {
        let Dyn = this.Dyn,
            card = this.smallCards[smallCardIndex],
            cardHomeBBox = new Dyn.mathUtil.Rect( card.getHomeBBox());
        return cardBBox.isOverlappingRect( cardHomeBBox ) ? cardHomeBBox : null;
    }

    /** if the card is outside the home area, return a new x,y location */
    getDragConstraintForCard( cardBBox, smallCardIndex ) {
        let Dyn = this.Dyn,
            bigCardsViewBounds = this.container.big.bounds,
            homeViewBounds = this.bounds;
        if( bigCardsViewBounds.isOverlappingRect( cardBBox )) {
            if( ! bigCardsViewBounds.isPointInRect( cardBBox.centerX(), cardBBox.centerY())) {
                return {    // constrain card center to the inside of the big card view
                    x: Dyn.mathUtil.clipToMinMax(
                        bigCardsViewBounds.left() - cardBBox.width / 2,
                        bigCardsViewBounds.right() - cardBBox.width / 2, cardBBox.x ),
                    y: Dyn.mathUtil.clipToMinMax(
                        bigCardsViewBounds.top() - cardBBox.height / 2,
                        bigCardsViewBounds.bottom() - cardBBox.width / 2, cardBBox.y ),
                };
            }
        } else if( ! homeViewBounds.isPointInRect( cardBBox.centerX(), cardBBox.centerY() )) {
            // move card back to home if it is outside both big cards and home views
            let card = this.smallCards[smallCardIndex],
                cardHomeBBox = new Dyn.mathUtil.Rect( card.getHomeBBox());
            return { x:cardHomeBBox.x, y:cardHomeBBox.y, animateHome:true };
        }
        return null;
    }

    // add glow effect to one card or part of a card, only one element glows at a time
    addGlow( element ) {
        this.removeGlow();
        this.glowElement = element.glow( {
            color: element.attr('stroke'),
            colorwidth: this.Dyn.plotUtil.glowWidth
        });
    }

    // remove the glow, if any
    removeGlow() {
        this.glowElement?.remove();
        this.glowElement = null;
    }

    getHomeState() {
        return {
            plot: this.allCardsAreHome() ? "home" : "leftHome"
        };
    }

    isInHomeState() {
        switch( this.container.state.plot ) {
            case "home":
            case "leftHome":
                return true;
        }
        return false; // cards may be in big cards or in play area.
    }

    saveCards() {
        let Dyn = this.Dyn,
            data = [],
            hasData = false;
        // save custom card location only for cards that need it:
        //  cards that were dragged from home, or dragged within plot bin.
        this.smallCards.forEach( (card, cardIndex) => {
            if( card.isDragged && ! this.container.big.isCardInBigCardArea( card.caseIndex )) {
                data.push( card.save());
                Dyn.assertFiniteNumeric( data[data.length-1].x );
                Dyn.assertFiniteNumeric( data[data.length-1].y );
                hasData = true;
            } else {
                data.push({});
            }
        });
        return hasData ? data : [];
    }

    restoreCards( data ) {
        let Dyn = this.Dyn,
            speed = this.container.slowAnimation();
        if( this.isInHomeState()) {
            this.resetSmallCards(); // animate all cards home (out of plot or big card area)
        }
        // restore those cards that are in a custom (dragged) position (see saveCards())
        data?.forEach( (xy, index) => {
            if( Dyn.mathUtil.isInIntegerRange( index, 0, this.smallCards.length )) {
                this.smallCards[index].restore( xy, true, speed, this.container );
            }
        });
        // update the state from "home" to/from "leftHome" (cards in custom-dragged position)
        this.container.updateHomeState();
    }

    /** get the small card index from a case index; returns -1 if case filtered out, etc */
    caseIndexToSmallCardIndex( caseIndex ) {
        let smallCardIndex = this.caseToCardIndex[ caseIndex ];
        if( smallCardIndex >= 0 && smallCardIndex < this.smallCards.length ) { // make sure it was not filtered out
            return smallCardIndex;
        }
        return -1;
    }

    getSmallCard( smallCardIndex ) {
        return this.smallCards[smallCardIndex];
    }

    /* show or hide the outlines of the small cards, which are normally visible in the card home area */
    showHideOutlines( wantVisible ) {
        this.showingOutlines = Boolean(wantVisible); // save state (for possible future use)
        this.smallCards.forEach( (card) =>
            this.Dyn.plotUtil.showOrHideElement( card.outline, this.showingOutlines )
        );
    }
}
